import React from 'react';
import { Shield, Users, Lock, Edit, CheckCircle, Info, Calendar, Activity } from 'lucide-react';
import Button from '../../components/ui/Button';

const RoleDetails = ({ role, onClose, onEdit }) => {
    // Organizar permisos por módulos para mostrar mejor
    const organizarPermisosPorModulos = (permisos) => {
        const modulos = {
            'Usuarios': [],
            'Roles': [],
            'Productos': [],
            'Ventas': [],
            'Reportes': []
        };

        if (permisos && Array.isArray(permisos)) {
            permisos.forEach(permiso => {
                if (permiso.includes('usuarios_')) modulos['Usuarios'].push(permiso);
                else if (permiso.includes('roles_')) modulos['Roles'].push(permiso);
                else if (permiso.includes('productos_')) modulos['Productos'].push(permiso);
                else if (permiso.includes('ventas_')) modulos['Ventas'].push(permiso);
                else if (permiso.includes('reportes_')) modulos['Reportes'].push(permiso);
            });
        }

        return modulos;
    };

    const permisosPorModulos = organizarPermisosPorModulos(role.permisos);

    const formatearPermiso = (permiso) => {
        const partes = permiso.split('_');
        const accion = partes[1];

        const acciones = {
            'ver': 'Ver',
            'crear': 'Crear',
            'editar': 'Editar',
            'eliminar': 'Eliminar',
            'cancelar': 'Cancelar',
            'exportar': 'Exportar',
            'avanzados': 'Avanzados'
        };

        return acciones[accion] || accion;
    };

    const getModuleIcon = (modulo) => {
        switch (modulo) {
            case 'Usuarios':
                return <Users size={16} />;
            case 'Roles':
                return <Shield size={16} />;
            case 'Productos':
                return <Lock size={16} />;
            case 'Ventas':
                return <Activity size={16} />;
            case 'Reportes':
                return <Info size={16} />;
            default:
                return <Lock size={16} />;
        }
    };

    const getNivelColor = (nivel) => {
        if (nivel >= 8) return 'nivel-alto';
        if (nivel >= 5) return 'nivel-medio';
        return 'nivel-bajo';
    };

    return (
        <div className="role-details">
            {/* Header del rol */}
            <div className="role-details-header">
                <div className="role-avatar-large">
                    <Shield size={32} />
                </div>
                <div className="role-main-info">
                    <h2 className="role-name">{role.nombre}</h2>
                    <p className="role-description">
                        {role.descripcion || 'Sin descripción disponible'}
                    </p>
                    <div className="role-status">
                        <span className={`status-indicator ${role.activo ? 'active' : 'inactive'}`}>
                            {role.activo ? (
                                <>
                                    <CheckCircle size={16} />
                                    Activo
                                </>
                            ) : (
                                <>
                                    <Lock size={16} />
                                    Inactivo
                                </>
                            )}
                        </span>
                    </div>
                </div>
                <div className="header-actions">
                    <Button
                        variant="secondary"
                        onClick={onEdit}
                        size="small"
                    >
                        <Edit size={16} />
                        Editar
                    </Button>
                </div>
            </div>

            {/* Contenido de detalles */}
            <div className="role-details-content">
                {/* Información general */}
                <div className="details-section">
                    <h3 className="section-title">
                        <Info size={18} />
                        Información General
                    </h3>
                    <div className="details-grid">
                        <div className="detail-item">
                            <span className="detail-label">Nivel de Autoridad</span>
                            <span className={`detail-value nivel-badge ${getNivelColor(role.nivel)}`}>
                                Nivel {role.nivel || 1}
                                {role.nivel >= 8 && ' (Alto)'}
                                {role.nivel >= 5 && role.nivel < 8 && ' (Medio)'}
                                {role.nivel < 5 && ' (Básico)'}
                            </span>
                        </div>
                        <div className="detail-item">
                            <span className="detail-label">Usuarios Asignados</span>
                            <span className="detail-value">
                                <Users size={16} />
                                {role.usuarios_count || 0} usuarios
                            </span>
                        </div>
                        <div className="detail-item">
                            <span className="detail-label">Total de Permisos</span>
                            <span className="detail-value">
                                <Lock size={16} />
                                {role.permisos?.length || 0} permisos
                            </span>
                        </div>
                        <div className="detail-item">
                            <span className="detail-label">Fecha de Creación</span>
                            <span className="detail-value">
                                <Calendar size={16} />
                                {role.fecha_creacion
                                    ? new Date(role.fecha_creacion).toLocaleDateString('es-ES')
                                    : 'No disponible'
                                }
                            </span>
                        </div>
                    </div>
                </div>

                {/* Permisos del rol */}
                <div className="details-section">
                    <h3 className="section-title">
                        <Lock size={18} />
                        Permisos Asignados
                    </h3>

                    {role.permisos && role.permisos.length > 0 ? (
                        <div className="permissions-details">
                            {Object.entries(permisosPorModulos).map(([modulo, permisos]) => (
                                permisos.length > 0 && (
                                    <div key={modulo} className="permission-module-detail">
                                        <div className="module-header-detail">
                                            {getModuleIcon(modulo)}
                                            <h4>{modulo}</h4>
                                            <span className="permissions-count">
                                                {permisos.length} permisos
                                            </span>
                                        </div>
                                        <div className="module-permissions-list">
                                            {permisos.map((permiso) => (
                                                <div key={permiso} className="permission-tag">
                                                    <CheckCircle size={12} />
                                                    {formatearPermiso(permiso)}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )
                            ))}
                        </div>
                    ) : (
                        <div className="no-permissions">
                            <Lock size={32} />
                            <p>Este rol no tiene permisos asignados</p>
                        </div>
                    )}
                </div>

                {/* Estadísticas y métricas */}
                <div className="details-section">
                    <h3 className="section-title">
                        <Activity size={18} />
                        Estadísticas
                    </h3>
                    <div className="stats-grid">
                        <div className="stat-item">
                            <div className="stat-value">{role.usuarios_count || 0}</div>
                            <div className="stat-label">Usuarios Activos</div>
                        </div>
                        <div className="stat-item">
                            <div className="stat-value">{role.permisos?.length || 0}</div>
                            <div className="stat-label">Permisos Totales</div>
                        </div>
                        <div className="stat-item">
                            <div className="stat-value">{role.nivel || 1}</div>
                            <div className="stat-label">Nivel de Autoridad</div>
                        </div>
                        <div className="stat-item">
                            <div className="stat-value">
                                {role.activo ? 'Sí' : 'No'}
                            </div>
                            <div className="stat-label">Estado Activo</div>
                        </div>
                    </div>
                </div>

                {/* Advertencias si las hay */}
                {(!role.activo || (role.usuarios_count > 0 && role.permisos?.length === 0)) && (
                    <div className="details-section warning">
                        <div className="warning-content">
                            <Info size={20} />
                            <div>
                                <h4>Advertencias</h4>
                                <ul>
                                    {!role.activo && (
                                        <li>Este rol está inactivo y no puede ser asignado a usuarios</li>
                                    )}
                                    {role.usuarios_count > 0 && role.permisos?.length === 0 && (
                                        <li>Este rol tiene usuarios asignados pero no tiene permisos</li>
                                    )}
                                </ul>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="role-details-footer">
                <Button
                    variant="secondary"
                    onClick={onClose}
                >
                    Cerrar
                </Button>
                <Button
                    onClick={onEdit}
                >
                    <Edit size={16} />
                    Editar Rol
                </Button>
            </div>
        </div>
    );
};

export default RoleDetails;